import { patientImportFields, type PatientImportField } from './dto/patients-import.js';
import type { Language } from './enums.js';

/**
 * Reconnaissance des en-têtes d'un fichier de patients, en français, en allemand et en anglais.
 *
 * Un cabinet luxembourgeois reçoit des exports dans les trois langues, quelle que soit celle de
 * son écran : toutes les formes sont donc reconnues pour tout le monde. Le premier libellé de
 * chaque langue est celui du **modèle** téléchargeable — ce que l'on propose, on le reconnaît.
 *
 * La comparaison se fait sur une clé réduite : parenthèses et crochets retirés (« Date de
 * naissance (JJ/MM/AAAA) »), accents et casse effacés, tout ce qui n'est ni lettre ni chiffre
 * supprimé. « E-mail », « Email » et « e mail » sont la même colonne.
 *
 * Un libellé n'est jamais rattaché à deux champs, et rien d'ambigu n'y entre : « N° » seul est plus
 * souvent l'identifiant du logiciel d'origine que le numéro de la rue, il reste donc à rattacher à
 * la main.
 */
const HEADERS: Readonly<Record<PatientImportField, Readonly<Record<Language, readonly string[]>>>> =
  {
    lastName: {
      fr: ['Nom', 'Nom de famille', 'Nom du patient', 'Nom de naissance', 'Nom patient'],
      de: ['Nachname', 'Familienname', 'Name', 'Zuname'],
      en: ['Last name', 'Surname', 'Family name', 'Lastname', 'Name'],
    },
    firstName: {
      fr: ['Prénom', 'Prénoms', 'Prénom du patient'],
      de: ['Vorname', 'Vornamen'],
      en: ['First name', 'Given name', 'Firstname', 'Forename'],
    },
    birthDate: {
      fr: ['Date de naissance', 'Naissance', 'Date naissance', 'Né le', 'Née le', 'DDN'],
      de: ['Geburtsdatum', 'Geboren am', 'Geburtstag', 'Geboren'],
      en: ['Date of birth', 'Birth date', 'Birthdate', 'DOB', 'Birthday'],
    },
    sex: {
      fr: ['Sexe', 'Genre', 'Civilité'],
      de: ['Geschlecht', 'Anrede'],
      en: ['Sex', 'Gender'],
    },
    email: {
      fr: ['E-mail', 'Courriel', 'Adresse e-mail', 'Adresse électronique', 'Mail'],
      de: ['E-Mail', 'E-Mail-Adresse'],
      en: ['Email', 'Email address'],
    },
    phone: {
      fr: ['Téléphone', 'Tél', 'Téléphone fixe', 'Fixe', 'Téléphone domicile'],
      de: ['Telefon', 'Telefonnummer', 'Festnetz'],
      en: ['Phone', 'Telephone', 'Phone number', 'Home phone', 'Landline'],
    },
    mobilePhone: {
      fr: ['Mobile', 'Portable', 'GSM', 'Téléphone portable', 'Téléphone mobile'],
      de: ['Handy', 'Mobil', 'Mobiltelefon', 'Mobilnummer', 'Handynummer'],
      en: ['Mobile phone', 'Cell phone', 'Cell', 'Cellphone'],
    },
    street: {
      fr: ['Rue', 'Adresse', 'Adresse postale', 'Voie'],
      de: ['Straße', 'Strasse', 'Anschrift'],
      en: ['Street', 'Address', 'Street address', 'Address line 1'],
    },
    houseNumber: {
      fr: ['Numéro de rue', 'N° de rue', 'Numéro de maison'],
      de: ['Hausnummer', 'Hausnr'],
      en: ['House number', 'Street number'],
    },
    addressDetails: {
      fr: ['Complément d’adresse', "Complément d'adresse", 'Complément', 'Adresse complément'],
      de: ['Adresszusatz', 'Zusatz'],
      en: ['Address details', 'Address line 2', 'Address 2'],
    },
    postalCode: {
      fr: ['Code postal', 'CP', 'NPA'],
      de: ['Postleitzahl', 'PLZ'],
      en: ['Postal code', 'Postcode', 'Zip code', 'Zip'],
    },
    city: {
      fr: ['Ville', 'Localité', 'Commune'],
      de: ['Ort', 'Wohnort', 'Stadt', 'Ortschaft'],
      en: ['City', 'Town', 'Locality'],
    },
    country: {
      fr: ['Pays', 'Pays de résidence'],
      de: ['Land', 'Wohnsitzland'],
      en: ['Country', 'Country of residence'],
    },
    ssn: {
      fr: [
        'Matricule',
        'Matricule CNS',
        'N° CNS',
        'Numéro de sécurité sociale',
        'N° de sécurité sociale',
        'N° sécurité sociale',
        'Sécurité sociale',
        'NSS',
        'NIR',
        'NISS',
      ],
      de: ['Sozialversicherungsnummer', 'Matrikel', 'Matrikelnummer', 'Versicherungsnummer'],
      en: ['Social security number', 'SSN', 'Social security', 'National insurance number'],
    },
    ssnCountry: {
      fr: ['Pays du matricule', 'Pays de sécurité sociale', 'Pays sécurité sociale'],
      de: ['Land der Sozialversicherung', 'Land der Matrikel', 'Land Matrikel'],
      en: ['Social security country', 'SSN country', 'Country of social security'],
    },
    profession: {
      fr: ['Profession', 'Métier', 'Activité professionnelle'],
      de: ['Beruf', 'Tätigkeit'],
      en: ['Occupation', 'Job'],
    },
    primaryPhysician: {
      fr: ['Médecin traitant', 'Médecin', 'Médecin généraliste', 'Généraliste'],
      de: ['Hausarzt', 'Behandelnder Arzt', 'Arzt'],
      en: ['General practitioner', 'GP', 'Family doctor', 'Primary physician', 'Doctor'],
    },
    notesAdmin: {
      fr: ['Notes administratives', 'Notes', 'Note', 'Remarques', 'Remarque', 'Commentaires'],
      de: ['Verwaltungsnotizen', 'Notizen', 'Bemerkungen', 'Anmerkungen', 'Kommentar'],
      en: ['Administrative notes', 'Comments', 'Comment', 'Remarks'],
    },
  };

/** La clé réduite sur laquelle deux en-têtes se comparent. */
export function normalizeImportHeader(header: string): string {
  return header
    .replace(/\([^)]*\)|\[[^\]]*\]/g, ' ')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Clé réduite → champ, toutes langues confondues, construit une fois. Un libellé qui désignerait
 * deux champs est une erreur du dictionnaire : elle éclate au chargement, pas chez un praticien
 * dont la colonne partirait dans le mauvais champ.
 */
function buildIndex(): ReadonlyMap<string, PatientImportField> {
  const index = new Map<string, PatientImportField>();
  for (const field of patientImportFields) {
    for (const label of Object.values(HEADERS[field]).flat()) {
      const key = normalizeImportHeader(label);
      const existing = index.get(key);
      if (existing !== undefined && existing !== field) {
        throw new Error(`En-tête ambigu entre ${existing} et ${field} : ${label}`);
      }
      index.set(key, field);
    }
  }
  return index;
}

const INDEX = buildIndex();

/**
 * Le champ de chaque colonne, ou `null` pour une colonne non reconnue. Un champ n'est proposé
 * qu'une fois : la première colonne qui le porte le prend, les suivantes restent à rattacher.
 */
export function recognizePatientImportHeaders(
  headers: readonly string[],
): (PatientImportField | null)[] {
  const taken = new Set<PatientImportField>();
  return headers.map((header) => {
    const field = INDEX.get(normalizeImportHeader(header));
    if (field === undefined || taken.has(field)) return null;
    taken.add(field);
    return field;
  });
}

/** Les en-têtes du modèle, dans la langue demandée, dans l'ordre des champs. */
export function patientImportTemplateHeaders(
  fields: readonly PatientImportField[],
  language: Language,
): string[] {
  return fields.map((field) => HEADERS[field][language][0] ?? field);
}
